import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

/**
 * Provides a per-page override channel for the global <Seo /> component.
 *
 * Detail pages call `usePageSeo({ title, description, image })` inside a
 * useEffect and the values propagate up to the Seo component, which merges
 * them with the site-wide defaults from /api/settings.
 *
 * Passing `null` (or leaving the fields empty) makes Seo fall back to the
 * global defaults — so the Home page needs to do nothing.
 */
const SeoContext = createContext({
  pageMeta: null,
  setPageMeta: () => {},
});

export function SeoProvider({ children }) {
  const [pageMeta, setPageMeta] = useState(null);
  const value = useMemo(() => ({ pageMeta, setPageMeta }), [pageMeta]);
  return <SeoContext.Provider value={value}>{children}</SeoContext.Provider>;
}

export function useSeoContext() {
  return useContext(SeoContext);
}

/**
 * Hook detail pages use to publish their SEO override.
 *
 * Pass `null` (or `undefined` fields) to fall back to the global values.
 * Runs on mount and whenever any of the fields change; automatically resets
 * to `null` when the page unmounts so navigating away restores the global
 * meta tags.
 */
export function usePageSeo(meta) {
  const { setPageMeta } = useContext(SeoContext);
  const key = meta ? `${meta.title || ""}|${meta.description || ""}|${meta.image || ""}` : "";
  useEffect(() => {
    setPageMeta(meta || null);
    return () => setPageMeta(null);
    // key captures a stable dep so we only fire on real changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
}

/**
 * Helper: derive a clean text excerpt from a raw HTML string. Used by pages
 * that don't have a dedicated meta_description field to still produce a
 * decent SEO snippet from the body/description.
 */
export function htmlToText(html, max = 160) {
  if (!html) return "";
  const text = String(html).replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
  if (text.length <= max) return text;
  return text.slice(0, max - 1).replace(/\s+\S*$/, "") + "…";
}
